import { useId } from 'react';
import { Area, AreaChart, ResponsiveContainer, Tooltip, YAxis } from 'recharts';

/**
 * 7일 추세 스파크라인.
 *
 * ★ 입력은 `lensDailyTrend` 가 만든 **쉼표 문자열**이다. Flutter 쪽은 커스텀
 * 위젯에 리스트를 넘길 수 없어 그 모양이 됐는데(CLAUDE.md), 정답지가 그 모양이라
 * 이식 1판에서는 **그대로 받는다.**
 *
 * ⚠️⚠️ **`-1` 은 점을 찍지 않는다.** 「0%」가 아니라 「그날은 판단 보류」다 —
 * 기간 창의 첫날은 대개 조각이라 회차가 한둘뿐이고, 그러면 완주율이 0% 아니면
 * 100% 밖에 안 나온다. ★ 실측에서 그 0% 가 선 왼쪽을 눌러 **가짜 급락**을
 * 그렸고 변화량을 「50.0%p 개선」으로 뒤집었다.
 *
 * ★★ Flutter 에서는 이것이 `CustomPainter` 였다(138줄). 여기서는 recharts 가
 * **데이터를 직접 받는다** — DSL 차트에 데이터 파라미터가 아예 없던 제약이
 * 여기서 풀린다.
 */

export interface SparkPoint {
  i: number;
  /** `null` 이면 그 날은 건너뛴다(`-1` 센티널). */
  v: number | null;
}

export function parseSeries(series: string): SparkPoint[] {
  if (series.trim() === '') return [];
  return series.split(',').map((raw, i) => {
    const v = Number.parseFloat(raw.trim());
    return { i, v: Number.isNaN(v) || v < 0 ? null : v };
  });
}

export function Sparkline({
  series,
  tone,
  color,
  format,
}: {
  series: string;
  /** 축색. ★ 판정이 없는 축(셋)은 이 색으로 그린다 — 회색보다 읽기 쉽다. */
  color?: string | undefined;
  /** `lensStatusFor` 가 낸 값. ★ KPI 카드와 **같은 판정 함수**를 써서 색이 어긋날 수 없다. */
  tone: string;
  format: (v: number) => string;
}) {
  const gid = useId().replace(/:/g, '');
  const data = parseSeries(series);
  const points = data.filter((d) => d.v !== null);

  if (points.length === 0) {
    return (
      <div className="flex h-12 items-center text-label-md text-muted-foreground">
        표본이 적어 그리지 않았습니다
      </div>
    );
  }

  const stroke =
    tone === 'good'
      ? 'var(--lens-success)'
      : tone === 'warn'
        ? 'var(--lens-warning)'
        : tone === 'bad'
          ? 'var(--lens-error)'
          : (color ?? 'var(--lens-text-muted)');

  return (
    <div className="h-16 w-full">
      <ResponsiveContainer width="100%" height="100%">
        <AreaChart data={data} margin={{ top: 4, right: 4, bottom: 4, left: 4 }}>
          <YAxis hide domain={['dataMin', 'dataMax']} />
          <Tooltip
            cursor={false}
            formatter={(v) => [format(Number(v)), '']}
            labelFormatter={(i) => `${data.length - Number(i)}일 전`}
            contentStyle={{
              background: 'var(--lens-surface)',
              border: '1px solid var(--lens-border)',
              borderRadius: 'var(--lens-radius-sm)',
              fontSize: 12,
              padding: '4px 8px',
            }}
          />
          {/* ★★ **면을 채운다**(2026-10-03 개편) — 선만으로는 다섯 축이
              색만 다른 똑같은 그림이었다(사용자 지적 — 「전부 똑같은 형태」). */}
          <defs>
            <linearGradient id={gid} x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor={stroke} stopOpacity={0.35} />
              <stop offset="100%" stopColor={stroke} stopOpacity={0.02} />
            </linearGradient>
          </defs>
          <Area
            type="monotone"
            dataKey="v"
            stroke={stroke}
            strokeWidth={2.5}
            fill={`url(#${gid})`}
            dot={{ r: 2, fill: stroke }}
            // ⚠️ `null` 을 이어 붙이지 않는다 — 판단 보류한 날을 선으로 메우면
            //    없는 값을 지어내는 것이다.
            connectNulls={false}
            isAnimationActive={false}
          />
        </AreaChart>
      </ResponsiveContainer>
    </div>
  );
}
